import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { hideOthers } from 'aria-hidden'
import {
  callAllHandlers,
  mergeRefs,
  useCallbackRef,
  useIds
} from '@mote/helpers'
import { KeyboardKey, noop } from '@mote/helpers/constants'
import { manager, useDialogManager } from './dialogManager'

export const DIALOG_TRANSITION_DURATION = 200

export function useDialogFunctions(props) {
  const {
    isOpen,
    onClose: onCloseProp = noop,
    id,
    maxWidth = 480,
    closeOnOverlayClick = true,
    closeOnEsc = true,
    useInert = true,
    isCentered = !props.isDrawer,
    isDrawer = false,
    placement = 'left',
    scrollBehavior = 'outside',
    onOverlayClick: onOverlayClickProp,
    onEsc
  } = props

  const dialogRef = useRef(null)
  const overlayRef = useRef(null)
  const mouseDownTarget = useRef(null)
  const closeAnimationTimeoutRef = useRef(null)
  const [isMounted, setIsMounted] = useState(false)
  const [headerMounted, setHeaderMounted] = useState(false)
  const [contentMounted, setContentMounted] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const shouldRender = useMemo(
    () => isOpen && isMounted && !isClosing,
    [isOpen, isMounted, isClosing]
  )

  const onClose = useCallbackRef(onCloseProp)

  const [dialogId, headerId, contentId] = useIds(
    id,
    'mote-dialog',
    'mote-dialog--header',
    'mote-dialog--content'
  )

  /**
   * Hook used to polyfill `aria-modal` for older browsers.
   * It uses `aria-hidden` to all other nodes.
   *
   * @see https://developer.paciellogroup.com/blog/2018/06/the-current-state-of-modal-dialog-accessibility/
   */
  useAriaHidden(dialogRef, isOpen && useInert)

  useDialogManager(dialogRef, isOpen)

  const handleOnClose = useCallback(
    origin => {
      setIsClosing(true)

      closeAnimationTimeoutRef.current = setTimeout(() => {
        setIsClosing(false)
        onClose(origin)
        closeAnimationTimeoutRef.current = null
      }, DIALOG_TRANSITION_DURATION)
    },
    [onClose]
  )

  const onMouseDown = useCallback(event => {
    mouseDownTarget.current = event.target
  }, [])

  const onKeyDown = useCallback(
    event => {
      if (event.keyCode === KeyboardKey.Escape) {
        event.stopPropagation()

        if (closeOnEsc) {
          handleOnClose('escape')
        }

        onEsc?.()
      }
    },
    [closeOnEsc, handleOnClose, onEsc]
  )

  const getDialogProps = useCallback(
    (props = {}, ref = null) => ({
      role: 'dialog',
      ...props,
      ref: mergeRefs(ref, dialogRef),
      isDrawer,
      placement: isDrawer ? placement : null,
      scrollBehavior,
      maxWidth,
      id: dialogId,
      tabIndex: -1,
      'aria-modal': true,
      'aria-labelledby': headerMounted ? headerId : undefined,
      'aria-describedby': contentMounted ? contentId : undefined,
      onClick: callAllHandlers(props.onClick, event => event.stopPropagation())
    }),
    [
      isDrawer,
      placement,
      maxWidth,
      contentId,
      contentMounted,
      dialogId,
      headerId,
      headerMounted,
      scrollBehavior
    ]
  )

  const onOverlayClick = useCallback(
    event => {
      event.stopPropagation()

      if (mouseDownTarget.current !== event.target) return

      if (!manager.isTopDialog(dialogRef)) return

      if (closeOnOverlayClick) {
        handleOnClose('overlay')
      }

      onOverlayClickProp?.()
    },
    [handleOnClose, closeOnOverlayClick, onOverlayClickProp]
  )

  const getDialogContainerProps = useCallback(
    (props = {}, ref = null) => ({
      ...props,
      scrollBehavior,
      isCentered,
      isDrawer,
      ref: mergeRefs(ref, overlayRef),
      onClick: callAllHandlers(props.onClick, onOverlayClick),
      onKeyDown: callAllHandlers(props.onKeyDown, onKeyDown),
      onMouseDown: callAllHandlers(props.onMouseDown, onMouseDown)
    }),
    [
      isDrawer,
      scrollBehavior,
      isCentered,
      onKeyDown,
      onMouseDown,
      onOverlayClick
    ]
  )

  const getDialogContentProps = useCallback(
    (props = {}) => ({
      ...props,
      scrollBehavior,
      id: contentId
    }),
    [scrollBehavior, contentId]
  )

  return {
    isOpen,
    isClosing,
    shouldRender,
    onClose: handleOnClose,
    headerId,
    setContentMounted,
    setHeaderMounted,
    setContainerMounted: setIsMounted,
    dialogRef,
    overlayRef,
    getDialogProps,
    getDialogContainerProps,
    getDialogContentProps
  }
}

/**
 * Dialog hook to polyfill `aria-modal`.
 *
 * It applies `aria-hidden` to elements behind the dialog
 * to indicate that they're `inert`.
 *
 * @param ref React ref of the node
 * @param shouldHide whether `aria-hidden` should be applied
 */
export function useAriaHidden(ref, shouldHide) {
  const currentElement = ref.current

  useEffect(() => {
    if (!ref.current || !shouldHide) return undefined

    return hideOthers(ref.current)
  }, [shouldHide, ref, currentElement])
}
